import { Dialog, useMediaQuery } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { canvasPreview } from './canvasPreview'
import Dropzone from 'react-dropzone'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPhotoFilm, faXmark, faCheck, faCropSimple } from '@fortawesome/free-solid-svg-icons'

// Upload de foto com RECORTE (crop) + COMPRESSÃO. Portado do DropFixImage do
// uen-gerenciador. Reduz o tamanho em KB (redimensiona pra no máx 1920x1080 e
// reexporta a 80% de qualidade) e deixa o usuário recortar no aspecto certo
// da carteirinha (7/9, foto 3x4).
//
// Props:
//   value     string  — base64 atual (mostra preview quando existe)
//   onChange  func     — chamado com a base64 final (recortada + comprimida)
//   aspect    number   — proporção do recorte (default 7/9, foto da carteirinha)

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight,
  )
}

function useDebounceEffect(fn, waitTime, deps) {
  useEffect(() => {
    const t = setTimeout(() => {
      fn.apply(undefined, deps)
    }, waitTime)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

export default function DropFixImage({ value, onChange, aspect = 7 / 9 }) {
  const [imgSrc, setImgSrc] = useState('')
  const previewCanvasRef = useRef(null)
  const imgRef = useRef(null)
  const [crop, setCrop] = useState()
  const [completedCrop, setCompletedCrop] = useState()
  const [open, setOpen] = useState(false)
  // Em celular o diálogo abre em TELA CHEIA (mais espaço pra recortar).
  const isMobile = useMediaQuery('(max-width:600px)')

  function onImageLoad(e) {
    if (aspect) {
      const { width, height } = e.currentTarget
      const pct = centerAspectCrop(width, height, aspect)
      setCrop(pct)
      // Já define o recorte concluído (em px) com o crop centralizado padrão —
      // assim a prévia e o botão "Salvar" funcionam SEM o usuário precisar
      // arrastar nada (antes o botão só aparecia depois de mexer no recorte).
      setCompletedCrop({
        unit: 'px',
        x: (pct.x / 100) * width,
        y: (pct.y / 100) * height,
        width: (pct.width / 100) * width,
        height: (pct.height / 100) * height,
      })
    }
  }

  // Comprime a imagem-fonte: redimensiona pra caber em 1920x1080 e reexporta
  // a 80% de qualidade. É o que derruba o tamanho em KB.
  async function compressImage(file) {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        reject(new Error('O arquivo enviado não é uma imagem.'))
        return
      }
      const reader = new FileReader()
      reader.onload = (event) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')

          const maxWidth = 1920
          const maxHeight = 1080
          let width = img.width
          let height = img.height

          if (width > maxWidth || height > maxHeight) {
            if (width / height > maxWidth / maxHeight) {
              height = Math.round((height * maxWidth) / width)
              width = maxWidth
            } else {
              width = Math.round((width * maxHeight) / height)
              height = maxHeight
            }
          }

          canvas.width = width
          canvas.height = height
          ctx.drawImage(img, 0, 0, width, height)

          canvas.toBlob(
            (blob) => {
              if (blob) {
                // DEBUG: tamanho ANTES (arquivo original) x DEPOIS (comprimido).
                const antesKB = (file.size / 1024).toFixed(1)
                const depoisKB = (blob.size / 1024).toFixed(1)
                const reducao = file.size > 0 ? (100 - (blob.size / file.size) * 100).toFixed(0) : '0'
                console.log(
                  `[DropFixImage] compressão: antes ${antesKB} KB (${img.width}x${img.height}) ` +
                  `→ depois ${depoisKB} KB (${width}x${height}) — ${reducao}% menor`
                )
                resolve(new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                }))
              } else {
                reject(new Error('Erro ao criar o arquivo comprimido.'))
              }
            },
            file.type,
            0.8,
          )
        }
        img.onerror = reject
        img.src = event.target.result
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const onDropa = async (files) => {
    if (!files || files.length === 0) return
    try {
      const comprimido = await compressImage(files[0])
      setCrop(undefined) // força recalcular o crop entre imagens
      const reader = new FileReader()
      reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''))
      reader.readAsDataURL(comprimido)
      setOpen(true)
    } catch (err) {
      console.error('[DropFixImage] falha ao processar imagem:', err)
    }
  }

  // Resolução máxima do maior lado do recorte final. Uma foto de carteirinha é
  // exibida/impressa pequena, então ~1000px é mais que suficiente — acima disso
  // só engorda o arquivo sem ganho visível (telas retina geram 2–3x à toa).
  const MAX_LADO_FINAL = 1000
  // Qualidade do JPEG final. 0.85 é visualmente idêntico ao 1.0 num retrato,
  // mas reduz MUITO os KB (1.0 = praticamente sem compressão).
  const QUALIDADE_FINAL = 0.90

  function onSalvarRecorte() {
    if (!previewCanvasRef.current) return

    const originalCanvas = previewCanvasRef.current

    // Limita a resolução do recorte (mantém proporção). Em retina o canvas de
    // preview sai com devicePixelRatio embutido (2–3x maior); reduzimos aqui.
    let w = originalCanvas.width
    let h = originalCanvas.height
    const maior = Math.max(w, h)
    if (maior > MAX_LADO_FINAL) {
      const f = MAX_LADO_FINAL / maior
      w = Math.round(w * f)
      h = Math.round(h * f)
    }

    // Fundo branco antes de desenhar (evita transparência virar preto no JPEG).
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    canvas.width = w
    canvas.height = h
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, w, h)
    ctx.drawImage(originalCanvas, 0, 0, w, h) // reescala o canvas de preview pro tamanho final

    canvas.toBlob((blob) => {
      if (!blob) return
      const reader = new FileReader()
      reader.readAsDataURL(blob)
      reader.onload = () => {
        // DEBUG: tamanho do RECORTE FINAL (é o que vai pro backend/banco em base64).
        const blobKB = (blob.size / 1024).toFixed(1)
        const base64KB = (String(reader.result).length / 1024).toFixed(1)
        console.log(
          `[DropFixImage] recorte final (JPEG q${QUALIDADE_FINAL}): ${blobKB} KB — ${canvas.width}x${canvas.height} ` +
          `(base64 enviado ~${base64KB} KB)`
        )
        if (onChange) onChange(reader.result)
        handleClose()
      }
    }, 'image/jpeg', QUALIDADE_FINAL)
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, 1, 0)
      }
    },
    100,
    [completedCrop],
  )

  const handleClose = () => {
    setImgSrc('')
    setOpen(false)
  }

  return (
    <div>
      <Dropzone onDrop={onDropa} accept={{ 'image/*': [] }} multiple={false}>
        {({ getRootProps, getInputProps }) => (
          <div
            {...getRootProps()}
            style={{
              cursor: 'pointer',
              border: '2px dashed #CBD5E1',
              borderRadius: 12,
              padding: value ? 12 : 20,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              background: '#F8FAFC',
              textAlign: 'center',
            }}
          >
            <input {...getInputProps()} />
            {value ? (
              <>
                <img
                  src={value}
                  alt="Foto"
                  style={{ width: 84, height: 108, objectFit: 'cover', borderRadius: 8, border: '1px solid #E2E8F0' }}
                />
                <span style={{ fontSize: '0.82rem', color: '#475569', fontWeight: 600 }}>
                  Trocar foto
                </span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPhotoFilm} size="lg" color="#059669" />
                <span style={{ fontSize: '0.88rem', color: '#475569', fontWeight: 600 }}>
                  Clique para anexar sua foto
                </span>
                <span style={{ fontSize: '0.74rem', color: '#94A3B8' }}>
                  Você vai poder recortar antes de salvar
                </span>
              </>
            )}
          </div>
        )}
      </Dropzone>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ style: { borderRadius: isMobile ? 0 : 16, overflow: 'hidden' } }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: isMobile ? '100%' : 'auto',
            maxHeight: isMobile ? '100%' : '92vh',
            background: '#fff',
            fontFamily: "'Inter', 'Roboto', system-ui, sans-serif",
          }}
        >
          {/* Header */}
          <div style={dz.header}>
            <span style={dz.headerTitle}>
              <FontAwesomeIcon icon={faCropSimple} color="#059669" /> Ajustar foto
            </span>
            <button onClick={handleClose} aria-label="Fechar" style={dz.closeBtn}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          {/* Corpo (rola se precisar) */}
          <div style={dz.body}>
            <p style={dz.hint}>
              Arraste e ajuste o quadro. <b>O que estiver dentro</b> vira a sua foto.
            </p>

            {!!imgSrc && (
              <div style={dz.cropStage}>
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={aspect}
                  keepSelection
                >
                  <img
                    ref={imgRef}
                    alt="Recorte"
                    src={imgSrc}
                    style={{
                      maxWidth: '100%',
                      maxHeight: isMobile ? '48vh' : 360,
                      borderRadius: 6,
                      objectFit: 'contain',
                      display: 'block',
                    }}
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
              </div>
            )}

            {/* Prévia */}
            <div style={dz.previewRow}>
              <div style={{ ...dz.previewBox, width: isMobile ? 74 : 92, height: isMobile ? 95 : 118 }}>
                <canvas ref={previewCanvasRef} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </div>
              <div style={dz.previewText}>
                <b style={{ color: '#0F172A' }}>Prévia</b>
                <span>É assim que vai sair na carteirinha.</span>
              </div>
            </div>
          </div>

          {/* Rodapé fixo */}
          <div style={dz.footer}>
            <button onClick={handleClose} style={dz.btnCancel}>Cancelar</button>
            <button
              onClick={onSalvarRecorte}
              disabled={!completedCrop}
              style={{
                ...dz.btnSave,
                background: completedCrop ? 'linear-gradient(135deg,#059669,#10B981)' : '#94A3B8',
                cursor: completedCrop ? 'pointer' : 'not-allowed',
              }}
            >
              <FontAwesomeIcon icon={faCheck} /> Salvar foto
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  )
}

// Estilos do diálogo de recorte (responsivo).
const dz = {
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '13px 16px', borderBottom: '1px solid #E2E8F0', flexShrink: 0, background: '#fff',
  },
  headerTitle: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    fontSize: '1rem', fontWeight: 800, color: '#0F172A',
  },
  closeBtn: {
    border: 'none', background: '#F1F5F9', width: 34, height: 34, borderRadius: '50%',
    cursor: 'pointer', color: '#475569', display: 'grid', placeItems: 'center', fontSize: 15,
  },
  body: {
    flex: 1, overflowY: 'auto', padding: 16, background: '#F8FAFC',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
  },
  hint: {
    margin: 0, fontSize: '0.86rem', color: '#475569', textAlign: 'center',
    lineHeight: 1.5, maxWidth: 420,
  },
  cropStage: {
    width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center',
    background: '#0F172A', borderRadius: 12, padding: 8, boxSizing: 'border-box',
  },
  previewRow: { display: 'flex', alignItems: 'center', gap: 12 },
  previewBox: {
    background: '#fff', border: '2px solid #E2E8F0', borderRadius: 8, overflow: 'hidden', flexShrink: 0,
  },
  previewText: {
    display: 'flex', flexDirection: 'column', gap: 2,
    fontSize: '0.8rem', color: '#64748B', lineHeight: 1.45,
  },
  footer: {
    display: 'flex', gap: 10, padding: '12px 16px', borderTop: '1px solid #E2E8F0',
    background: '#fff', flexShrink: 0,
  },
  btnCancel: {
    flex: '0 0 auto', padding: '13px 18px', borderRadius: 10, border: '1px solid #E2E8F0',
    background: '#fff', color: '#475569', fontWeight: 700, fontSize: '0.92rem',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  btnSave: {
    flex: 1, padding: '13px 18px', borderRadius: 10, border: 'none', color: '#fff',
    fontWeight: 700, fontSize: '0.95rem', fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  },
}
